import {useContext} from 'react';
import {useHistory} from 'react-router-dom';

import {
  billingStore,
  selectWBAgentBillingAccess,
  useWBAgentBilling,
  type WBAgentBillingReadyState,
  type WBAgentBillingState,
} from '../../../agentChat/billing';
import {useBannerDismissal} from '../../../agentChat/useBannerDismissal';
import {
  accountSettingsBillingTab,
  contactSalesPricing,
} from '../../../util/urls';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {AccountPlanType} from '../../Search/SearchNav/types';
import {type AgentChatBannerProps} from './AgentChatBanner';

type BannerContext = {
  isEnterprise: boolean;
  isAcademic: boolean;
  goToBilling?: () => void;
  contactSales: () => void;
};

/**
 * BillingBanner is the banner to show plus, when dismissable, its own dismissal
 * identity.
 */
type BillingBanner = Omit<AgentChatBannerProps, 'onDismiss'> & {
  dismiss?: {noticeId: string; resetAt: number | null};
};

/**
 * useWBAgentBillingBanner maps the billing verdict and snapshot to the props for
 * the notice explaining why the agent is blocked, via `selectWBAgentBillingBanner`,
 * or null when none applies. The consumer renders and positions it.
 */
export function useWBAgentBillingBanner(): AgentChatBannerProps | null {
  const billing = useWBAgentBilling();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const history = useHistory();

  const orgName = selectedAccount?.name ?? null;
  const isEnterprise = selectedAccount?.isEnterprise ?? false;
  const isAcademic =
    selectedAccount?.accountPlanType === AccountPlanType.Academic;
  const banner = selectWBAgentBillingBanner(billing, {
    isEnterprise,
    isAcademic,
    goToBilling:
      orgName != null
        ? () => history.push(accountSettingsBillingTab(orgName))
        : undefined,
    contactSales: () => {
      // oxlint-disable-next-line wandb/no-unprefixed-urls
      window.open(contactSalesPricing(), '_blank', 'noopener');
    },
  });

  // A dismissable banner carries its own notice id and reset anchor; a null id
  // (no banner, or a non-dismissable one) skips the dismissal query.
  const {dismissed, dismiss} = useBannerDismissal(
    banner?.dismiss?.noticeId ?? null,
    {resetAt: banner?.dismiss?.resetAt}
  );
  if (banner == null || (banner.dismiss != null && dismissed)) {
    return null;
  }

  const {dismiss: _dismiss, ...content} = banner;
  return {
    ...content,
    onDismiss: dismiss ?? undefined,
  };
}

/**
 * selectWBAgentBillingBanner maps a billing verdict and snapshot to the banner
 * to show, or null when none applies (accessible, or still resolving).
 */
export function selectWBAgentBillingBanner(
  billing: WBAgentBillingState,
  ctx: BannerContext
): BillingBanner | null {
  if (billing.status === 'error') {
    return {
      variant: 'error',
      title: "We couldn't load your billing details.",
      body: 'Try again in a moment.',
      trailingAction: {text: 'Retry', onClick: () => billingStore.refresh()},
    };
  }

  if (billing.status === 'resolving' || billing.status === 'unenforced') {
    return null;
  }

  // Billing is ready; an accessible account may still warrant an advisory.
  const access = selectWBAgentBillingAccess(billing);
  if (access.accessible) {
    return paygoAdvisoryBanner(billing, ctx);
  }

  switch (access.reason) {
    case 'resolving':
    case 'error':
      // Non-ready billing returns above; a ready snapshot never lands here.
      return null;
    case 'trial':
      return trialBanner(billing, ctx, access.expired);
    case 'cap':
      return capBanner(billing, ctx);
    case 'upgrade':
      return noAllotmentBanner(billing, ctx);
    case 'quota':
      return quotaBanner(billing, ctx);
    default: {
      const _exhaustive: never = access;
      void _exhaustive;
      return null;
    }
  }
}

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

// Spend amounts are arbitrary cents, so keep two decimals (e.g. "$1.50").
const USD_EXACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

// Stable id for the paygo advisory's server-side dismissal.
const PAYGO_NOTICE_ID = 'wbagent-paygo';

/**
 * paygoAdvisoryBanner renders the dismissable "now on pay-as-you-go" notice once
 * an accessible account has spent past its included allotment, or null when it
 * is still within the included credits or not on pay-as-you-go.
 */
function paygoAdvisoryBanner(
  billing: WBAgentBillingReadyState,
  ctx: BannerContext
): BillingBanner | null {
  if (!billing.isPaygoEnabled || billing.quotaUsed < billing.quotaLimit) {
    return null;
  }
  // NOTE: Enterprise paygo phrases this as "overage"; build that as a follow-up.
  if (ctx.isEnterprise) {
    return null;
  }

  const spend = exactDollars(billing.quotaUsed - billing.quotaLimit);
  return {
    variant: 'info',
    title: `You've used your ${dollars(billing.quotaLimit)} free AI credits.`,
    body: `You're now on pay-as-you-go. Current spend this month: ${spend}`,
    // Re-shows each billing period; the period start anchors the dismissal.
    dismiss: {noticeId: PAYGO_NOTICE_ID, resetAt: billing.billingPeriodStartAt},
  };
}

/** trialBanner is shown when a trial is exhausted or has expired. */
function trialBanner(
  billing: WBAgentBillingReadyState,
  ctx: BannerContext,
  expired: boolean
): BillingBanner {
  return {
    variant: 'error',
    title: expired
      ? 'Your W&B Agent trial has expired.'
      : `You've reached your ${dollars(billing.quotaLimit)} W&B Agent trial limit.`,
    body: 'To continue using W&B Agent, contact sales.',
    actions: [{text: 'Contact sales', onClick: ctx.contactSales}],
  };
}

/** capBanner is shown when a paygo account hits its hard spend cap. */
function capBanner(
  billing: WBAgentBillingReadyState,
  ctx: BannerContext
): BillingBanner {
  const date = resetDate(billing.billingPeriodEndAt);
  return {
    variant: 'error',
    title: `You've hit your ${dollars(billing.quotaSafetyLimit)} monthly W&B Agent spend limit.`,
    body:
      date != null
        ? `Want a higher cap? Talk to support or wait for your limit to reset on ${date}.`
        : 'Want a higher cap? Talk to support.',
    actions: [{text: 'Contact support', onClick: ctx.contactSales}],
  };
}

/**
 * noAllotmentBanner is shown when the plan includes no agent allotment, so
 * access requires moving to a paid plan.
 */
function noAllotmentBanner(
  billing: WBAgentBillingReadyState,
  ctx: BannerContext
): BillingBanner {
  if (ctx.isAcademic) {
    return {variant: 'upgrade', title: 'Agent features require a paid plan.'};
  }

  return {
    variant: 'upgrade',
    title: 'Agent features require a paid plan.',
    body: 'Upgrade to Pro or Enterprise to start using agents.',
    actions: [
      ...(ctx.goToBilling != null
        ? [{text: 'Upgrade', onClick: ctx.goToBilling}]
        : []),
      {text: 'Contact sales', onClick: ctx.contactSales},
    ],
  };
}

/**
 * quotaBanner shows when a free monthly allotment is exhausted with paygo off.
 * Only non-enterprise plans reach it (allow-listed Academic and Free, plus Pro
 * during the paygo rollout); a paid enterprise is on paygo and a trial
 * enterprise routes to trialBanner, so neither lands here.
 */
function quotaBanner(
  billing: WBAgentBillingReadyState,
  ctx: BannerContext
): BillingBanner {
  const date = resetDate(billing.billingPeriodEndAt);
  const resets = date != null ? `Resets ${date}.` : undefined;
  if (ctx.isAcademic) {
    return {
      variant: 'error',
      title: `You've reached your ${dollars(billing.quotaLimit)} monthly AI usage limit.`,
      body: resets,
    };
  }

  return {
    variant: 'error',
    title: `You've used your ${dollars(billing.quotaLimit)} of included Agent usage.`,
    body: resets,
  };
}

/** dollars formats a cent amount as a whole-dollar USD string (e.g. 5000 -> "$50"). */
function dollars(cents: number): string {
  return USD.format(cents / 100);
}

/** exactDollars formats a cent amount as USD with cents (e.g. 150 -> "$1.50"). */
function exactDollars(cents: number): string {
  return USD_EXACT.format(cents / 100);
}

/** resetDate formats the reset date (e.g. "Jul 3"), or null when unknown. */
function resetDate(epochMs: number | null): string | null {
  if (epochMs == null) {
    return null;
  }

  return new Date(epochMs).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
